import * as React from 'react';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faHeart } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getMessagesUrl, getNotificationsUrl } from '@/utils/urls';
import { getMyBidItemIds } from '@/redux/modules/bidderBiddingInfoAll';
import { getTotalWonItemsCount } from '@/redux/modules/wonItemsCount/wonItemsCount.selectors';
import { getUnreadInboxCount } from '@/redux/modules/conversationFolders';
import { getUserFirstName, logOutBidder } from '@/redux/modules/user';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import CountBadge from '@liveauctioneers/caterwaul-components/lib/CountBadge/CountBadge';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import NotificationsContainer from '@/components/Header/Notifications/NotificationsContainer';
import PaddleIconSvg from './PaddleIconSvg';
import Popover from '@liveauctioneers/caterwaul-components/lib/Popover/Popover';
import styled from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

type Props = {
    parent: React.ReactNode;
};

const LoggedInMenu = ({ parent }: Props) => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const firstName = useAppSelector(getUserFirstName);
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const isMobile = useIsNarrowerThan(768);
    const { upcomingBidItemIds = [] } = useAppSelector(getMyBidItemIds);
    const upcomingBids = upcomingBidItemIds.length;
    const unreadCount = useAppSelector(getUnreadInboxCount);
    const wonItemCount = useAppSelector(getTotalWonItemsCount);

    const logOut = React.useCallback(() => dispatch(logOutBidder()), [dispatch]);
    return (
        <StyledLoggedInMenu>
            {!whiteLabel && <NotificationsContainer mobile={isMobile} parent={parent} pathname={pathname} />}
            <StyledMenuItemWrapper>
                <Link key="saved-items" route to="/dashboard/saved-items">
                    <StyledMenuItem>
                        <StyledMenuItemFAI icon={faHeart} />
                        <StyledMenuItemText>
                            <FormattedMessage id="userMenu.favorites" />
                        </StyledMenuItemText>
                    </StyledMenuItem>
                </Link>
            </StyledMenuItemWrapper>
            {!isMobile && (
                <StyledMenuItemWrapper>
                    <StyledMyBids key="my-bids" route to="/dashboard/my-bids">
                        <StyledMenuItem>
                            <PaddleIconSvg />
                            <StyledMenuItemText>
                                <FormattedMessage id="userMenu.my_bids" />
                            </StyledMenuItemText>
                            <StyledCountBadge count={upcomingBids} grey />
                        </StyledMenuItem>
                    </StyledMyBids>
                </StyledMenuItemWrapper>
            )}
            <Popover
                childrenClickClosesPopover
                hover={false}
                placement="bottom"
                trigger={
                    <StyledMenuItemWrapper>
                        <StyledMenuItem>
                            <StyledMenuItemFAI icon={faUser} />
                            <StyledMenuItemText>
                                {Boolean(firstName) && firstName.length < 9 ? (
                                    <FormattedMessage id="userMenu.greeting" values={{ firstName }} />
                                ) : (
                                    <FormattedMessage id="userMenu.my_account" />
                                )}
                            </StyledMenuItemText>
                        </StyledMenuItem>
                        <StyledCaretFAI icon={faCaretDown} />
                    </StyledMenuItemWrapper>
                }
            >
                <StyledContent>
                    {isMobile && (
                        <StyledNavigationLink key="my-bids" route to="/dashboard/my-bids">
                            <FormattedMessage id="userMenu.my_bids" />
                            <StyledNumber count={upcomingBids} grey />
                        </StyledNavigationLink>
                    )}
                    {!whiteLabel && (
                        <StyledNavigationLink key="my-auctions" route to="/dashboard/my-auctions">
                            <FormattedMessage id="my_auctions" />
                        </StyledNavigationLink>
                    )}
                    <StyledNavigationLink key="won-items" route to="/dashboard/won-items">
                        <FormattedMessage id="won_items" />
                        <StyledNumber count={wonItemCount} />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="messages" route to={getMessagesUrl()}>
                        <FormattedMessage id="messages" />
                        <StyledNumber count={unreadCount} />
                    </StyledNavigationLink>
                    <StyledNavigationLink key="profile" route to="/dashboard/profile">
                        <FormattedMessage id="account_settings" />
                    </StyledNavigationLink>
                    {!whiteLabel && (
                        <>
                            <StyledNavigationLink key="followed_searches" route to="/dashboard/search-and-alerts/">
                                <FormattedMessage id="followed_searches" />
                            </StyledNavigationLink>

                            <StyledNavigationLink key="followed_auctioneers" route to="/dashboard/followed-auctioneers">
                                <FormattedMessage id="followed_auctioneers" />
                            </StyledNavigationLink>
                        </>
                    )}
                    <StyledNavigationLink key="notifications" route to={getNotificationsUrl()}>
                        <FormattedMessage id="notificationsPage.title" />
                    </StyledNavigationLink>
                    <StyledLogoutLink key="log-out" onClick={logOut} to="">
                        <FormattedMessage id="log_out" />
                    </StyledLogoutLink>
                </StyledContent>
            </Popover>
        </StyledLoggedInMenu>
    );
};

export default React.memo<Props>(LoggedInMenu, isEqual);

const StyledLoggedInMenu = styled.div`
    border: none;
    background-color: ${({ theme }) => theme.colors.white};
    display: inline-flex;
    height: 44px;
    padding: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding-top: 9px;
    }

    @media (max-width: ${({ theme }) => `${theme.breakpoints.tabletNarrowWidthNum - 1}px`}) {
        right: 20px;
    }
`;

const StyledMyBids = styled(Link)`
    svg {
        height: 22px;
        min-height: 22px;
        color: ${({ theme }) => theme.colors.grey100};
    }

    &:hover {
        svg {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const StyledMenuItemWrapper = styled.div`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    cursor: pointer;
    border-left: 1px solid ${({ theme }) => theme.colors.grey400};
    padding: 4px 0;
    display: flex;

    @media (max-width: 768px) {
        border-left: none;
    }

    & > svg {
        color: ${({ theme }) => theme.colors.grey100};
    }

    :hover,
    :hover span {
        color: ${({ theme }) => theme.colors.blue200};

        svg {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }

    & > a {
        text-decoration: none !important;
    }
`;

const StyledMenuItem = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: center;
    width: 89px;
    min-width: 89px;

    @media (max-width: 768px) {
        width: 44px;
        min-width: 44px;
    }
`;

const StyledMenuItemText = styled.span`
    line-height: 12px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.grey100};
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    padding-top: 5px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const StyledMenuItemFAI = styled(FontAwesomeIcon)`
    align-self: center;
    font-size: 22px;
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledCaretFAI = styled(FontAwesomeIcon)`
    margin-left: -15px;
    margin-top: -5px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const StyledContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px 0 0;
`;

const StyledNavigationLink = styled(Link)`
    && {
        /* Link and text don't play well together. OP-621 is to fix that */
        color: ${({ theme }) => theme.colors.grey100};
        position: relative;
        cursor: pointer;
        font-family: ${({ theme }) => theme.typography.fontPrimary};
        font-size: 14px;
        letter-spacing: 0.5px;
        line-height: 43px;
        padding: 2px 20px;
        text-align: left;
        text-decoration: none;
        display: flex;
        flex-direction: row;
        align-items: center;

        :hover {
            color: ${({ theme }) => theme.colors.blue200};
            background-color: ${({ theme }) => theme.colors.grey500};
        }
    }
`;

const StyledLogoutLink = styled(StyledNavigationLink)`
    &&& {
        text-transform: uppercase;
        margin-top: 14px;
        text-align: center !important;
        border-top: 1px solid ${({ theme }) => theme.colors.grey400};
        color: ${({ theme }) => theme.colors.blue100};
        font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
        padding: 16px 20px;
        border-radius: 0 0 2px 2px;
        justify-content: center;

        &:hover {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const StyledCountBadge = styled(CountBadge)`
    position: absolute;
    left: 50%;
    top: -3px;
`;

const StyledNumber = styled(CountBadge)`
    margin-left: 5px;
    float: right;
`;
